import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Save, Download, User, Phone, Pill, HeartPulse, Languages, Accessibility, StickyNote, QrCode } from "lucide-react";
import { getLifeCard, saveLifeCard } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = {
  name: "",
  emergency_contact: "",
  allergies: "",
  medical_info: "",
  preferred_language: "English",
  accessibility_needs: "",
  notes: "",
};

export function LifeCard() {
  const { uid } = useApp();
  const { user } = useAuth();
  const effectiveId = user?.user_id || uid;
  const [card, setCard] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await getLifeCard(effectiveId);
        setCard({ ...empty, ...c, user_id: effectiveId });
      } catch (e) {
        setCard({ ...empty, user_id: effectiveId });
      } finally {
        setLoaded(true);
      }
    })();
  }, [effectiveId]);

  useEffect(() => {
    if (!loaded) return;
    if (user && !card.name) {
      setCard((c) => ({ ...c, name: user.name || c.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loaded]);

  const save = async () => {
    setSaving(true);
    try {
      await saveLifeCard({ ...card, user_id: effectiveId });
      toast.success("Life Card saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const printCard = () => {
    window.print();
  };

  return (
    <section id="life-card" className="relative py-20 md:py-28 border-t border-white/5">
      <div className="absolute inset-0 tac-grid-fine opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">STEP 07 // MY LIFE CARD</span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-3xl">
          Your emergency identity, on <span className="text-lime-accent">one card</span>.
        </h2>
        <p className="mt-3 max-w-2xl text-[color:var(--lg-text-2)]">
          Never enter information you don&apos;t want stored in this demo. Sensitive fields are optional.
        </p>

        <div className="mt-10 grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 glass rounded-md p-5 space-y-4">
            <Field icon={<User size={14} />} label="Name" value={card.name} onChange={(v) => setCard({ ...card, name: v })} testid="lc-name" />
            <Field icon={<Phone size={14} />} label="Emergency contact" value={card.emergency_contact} onChange={(v) => setCard({ ...card, emergency_contact: v })} testid="lc-contact" />
            <Field icon={<Pill size={14} />} label="Allergies" value={card.allergies} onChange={(v) => setCard({ ...card, allergies: v })} testid="lc-allergies" />
            <Field icon={<HeartPulse size={14} />} label="Important medical info" value={card.medical_info} onChange={(v) => setCard({ ...card, medical_info: v })} testid="lc-medical" textarea />
            <Field icon={<Languages size={14} />} label="Preferred language" value={card.preferred_language} onChange={(v) => setCard({ ...card, preferred_language: v })} testid="lc-language" />
            <Field icon={<Accessibility size={14} />} label="Accessibility needs" value={card.accessibility_needs} onChange={(v) => setCard({ ...card, accessibility_needs: v })} testid="lc-a11y" />
            <Field icon={<StickyNote size={14} />} label="Notes for responders" value={card.notes} onChange={(v) => setCard({ ...card, notes: v })} testid="lc-notes" textarea />
            <div className="flex gap-2 pt-2">
              <button className="btn-tactile primary" onClick={save} disabled={saving} data-testid="lc-save">
                <Save size={14} /> {saving ? "Saving…" : "Save Life Card"}
              </button>
              <button className="btn-tactile" onClick={printCard} data-testid="lc-download">
                <Download size={14} /> Download / Print
              </button>
            </div>
          </div>

          <div className="md:col-span-2">
            <CardPreview card={card} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ icon, label, value, onChange, textarea, testid }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)] mb-1">
        <span className="text-[color:var(--lg-cyan)]">{icon}</span> {label.toUpperCase()}
      </div>
      {textarea ? (
        <textarea
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
          data-testid={testid}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
          data-testid={testid}
        />
      )}
    </label>
  );
}

function CardPreview({ card }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative aspect-[1.586/1] rounded-lg overflow-hidden glass p-5"
      style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.05)" }}
      data-testid="lc-preview"
    >
      <div className="absolute inset-0 tac-grid-fine opacity-40" />
      <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[color:var(--lg-lime)]/10 blur-3xl" />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="font-mono-tac text-[9px] tracking-[0.28em] text-[color:var(--lg-lime)]">LIFEGRID // LIFE CARD</div>
          <div className="mt-1 font-display text-xl font-extrabold uppercase">{card.name || "Your Name"}</div>
        </div>
        <div className="h-10 w-10 rounded-sm border border-white/20 grid place-items-center text-[color:var(--lg-cyan)]">
          <QrCode size={22} />
        </div>
      </div>
      <div className="relative mt-4 grid grid-cols-2 gap-3 text-xs">
        <Cell label="EMERGENCY" v={card.emergency_contact || "—"} />
        <Cell label="LANGUAGE" v={card.preferred_language || "—"} />
        <Cell label="ALLERGIES" v={card.allergies || "—"} />
        <Cell label="ACCESSIBILITY" v={card.accessibility_needs || "—"} />
        <Cell label="MEDICAL" v={card.medical_info || "—"} span />
      </div>
      <div className="absolute bottom-3 left-5 right-5 flex items-center justify-between font-mono-tac text-[9px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
        <span>ID · {(card.user_id || "").slice(-6).toUpperCase() || "OFFLINE"}</span>
        <span>DEMO · NOT MEDICAL RECORD</span>
      </div>
    </motion.div>
  );
}

function Cell({ label, v, span }) {
  return (
    <div className={`border border-white/10 rounded-sm p-2 ${span ? "col-span-2" : ""}`}>
      <div className="font-mono-tac text-[9px] tracking-[0.24em] text-[color:var(--lg-text-3)]">{label}</div>
      <div className="mt-1 truncate">{v}</div>
    </div>
  );
}
